import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Env } from '../models/Env';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

@Injectable({
  providedIn: 'root',
})
export class EnvService {
  public currentEnvSubject: BehaviorSubject<Env>;
  public currentEnv: Observable<Env>;

  constructor(private http: HttpClient) {
    const savedEnv = localStorage.getItem('env');
    this.currentEnvSubject = new BehaviorSubject<Env>(
      savedEnv ? JSON.parse(savedEnv) : environment.prod,
    );
    this.currentEnv = this.currentEnvSubject.asObservable();
  }

  public get env() {
    return this.currentEnvSubject.value;
  }

  setEnvToProd() {
    localStorage.setItem('env', JSON.stringify(environment.prod));
    this.currentEnvSubject.next(environment.prod);
  }

  setEnvToDemo() {
    localStorage.setItem('env', JSON.stringify(environment.demo));
    this.currentEnvSubject.next(environment.demo);
  }

  checkDemo(version = '', platform = '') {
    return this.http
      .get(
        environment.prod.apiUrl +
          'demo-log?version=' +
          version +
          '&platform=' +
          platform,
      )
      .pipe();
  }

  resolveEnv(): Promise<void> {
    return new Promise((resolve) => {
      if (!Capacitor.isNativePlatform()) {
        this.setEnvToProd();
        resolve();
        return;
      }

      CapacitorApp.getInfo()
        .then((info) => {
          const version = info.version;
          const platform = Capacitor.getPlatform();
          this.checkDemoWithRetry(version, platform, 3).then(() => resolve());
        })
        .catch(() => {
          this.setEnvToProd();
          resolve();
        });
    });
  }

  private checkDemoWithRetry(
    version: string,
    platform: string,
    retriesLeft: number,
    delayMs = 800,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.checkDemo(version, platform).subscribe(
        (res) => {
          if (res && res['success']) {
            const is_demo = res['data'].is_demo;
            is_demo ? this.setEnvToDemo() : this.setEnvToProd();
          } else {
            this.setEnvToProd();
          }
          resolve();
        },
        (err) => {
          if (retriesLeft > 0) {
            setTimeout(() => {
              this.checkDemoWithRetry(
                version,
                platform,
                retriesLeft - 1,
                delayMs,
              ).then(resolve);
            }, delayMs);
          } else {
            this.setEnvToProd();
            resolve();
          }
        },
      );
    });
  }
}
